import { Component, inject, OnInit, signal } from '@angular/core';
import { PortfoAssetsComponent } from '../../components/portfo-assets/portfo-assets.component';
import { PortfoEtfComponent } from '../../components/portfo-etf/portfo-etf.component';
import { PortfoProfilesAssetComponent } from '../../components/portfo-profiles-asset/portfo-profiles-asset.component';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../components/core/components/base/base.component';
import { PortfolioDetail } from '../../../../data-access/models/portfolio-detail.model';
import { FundDataService } from '../../../../components/core/services/fund-data.service';
import { HOME_ROUTE, PENDING_TRANSACTIONS_ROUTE } from '../../../../data-access/constants/app-routes';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { PortfoPendingTransactionsComponent } from '../../components/portfo-pending-transactions/portfo-pending-transactions.component';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { Params } from '@angular/router';
import { TransactionService } from '../../../../components/core/services/transaction.service';
import { ITransaction_V2 } from '../../../../data-access/models/transaction.model';
import { SegmentItemsModel } from '@digipay/ngx-segmented-control/lib/models/types';

@Component({
  selector: 'app-portfo',
  standalone: true,
  imports: [
    PortfoEtfComponent,
    PortfoAssetsComponent,
    PortfoProfilesAssetComponent,
    PortfoPendingTransactionsComponent,
    NgxAppBarComponent,
    SpinnerComponent,
  ],
  templateUrl: './portfo.component.html',
  styleUrl: './portfo.component.scss',
})
export class PortfoComponent extends BaseComponent implements OnInit {
  assetData: {
    balance: number;
    portfolioDetails: PortfolioDetail[];
  } = {
    balance: 0,
    portfolioDetails: [],
  };
  loading = signal(false);
  walletBalance = signal(null);
  canWithdraw = signal<boolean>(false);
  canSee = signal(true);
  assetBalance = signal<number>(0);
  crowdBalance = signal<number>(0);
  ipoBalance = signal<number>(0);
  stockBalance = signal<number>(0);
  pageNumber = signal<number>(1);
  type = signal<string>('Buy');
  totalAmount = signal<number>(0);
  totalUnits = signal<number>(0);
  transactions = signal<ITransaction_V2[]>([]);
  loadingTransactions = signal<boolean>(true);
  state = signal<string>('BUY_TRANSACTION');
  filteredTransactions = signal<ITransaction_V2[]>([]);

  private transactionService = inject(TransactionService);
  private navigationService = inject(WealthNavigationService);

  constructor(private fundService: FundDataService) {
    super();
  }

  ngOnInit(): void {
    this.getFund();
    this.getWalletBalance();
    const params: Params = { count: 4, type: this.type() };
    this.getTransactions(params);
  }

  onBackHandler() {
    this.navigationService.navigate([HOME_ROUTE]);
  }

  getFund() {
    this.loading.set(true);
    this.fundService
      .getUserAssets(true)
      .pipe(takeUntil(this.destroyObservable))
      .subscribe((res) => {
        if (res && res.balance) {
          this.assetData = res;
          this.assetData.portfolioDetails = res.portfolioDetails.filter((x) => x.price);
          res.portfolioDetails.forEach((asset) => {
            if (asset.type === 'IPO') {
              this.ipoBalance.set(this.ipoBalance() + asset.price);
            } else if (asset.type === 'Stock') {
              this.stockBalance.set(this.stockBalance() + asset.price);
            } else if (asset.investmentType === 'CrowdFund') {
              this.crowdBalance.set(this.crowdBalance() + asset.price);
            } else {
              this.assetBalance.set(this.assetBalance() + asset.price);
            }
          });
        }
        this.loading.set(false);
      });
  }

  getWalletBalance() {
    this.fundService
      .getWalletBalance()
      .pipe(takeUntil(this.destroyObservable))
      .subscribe((res) => {
        if (res.result !== null) {
          this.walletBalance.set(res.result?.amount);
          this.canWithdraw.set(res.result?.canWithdraw);
        } else {
          this.walletBalance.set(res.result);
        }
      });
  }

  changeSeeStatus() {
    this.canSee.set(!this.canSee());
  }

  private getTransactions(params: Params) {
    this.transactionService
      .getRecentOrders(this.pageNumber(), params)
      .pipe(takeUntil(this.destroyObservable))
      .subscribe((res) => {
        if (res?.success) {
          this.transactions.set(res.result.details);
          if (this.type() === 'Sell') {
            this.totalUnits.set(this.transactions().reduce((total, transaction) => total + transaction.units, 0));
          } else {
            this.totalAmount.set(this.transactions().reduce((total, transaction) => total + transaction.amount, 0));
          }
        }
        this.loadingTransactions.set(false);
      });
  }

  updateFilter(segmentValue: SegmentItemsModel) {
    if (segmentValue.value != this.state()) {
      this.loadingTransactions.set(true);
      this.state.set(segmentValue.value.toString());
      this.totalAmount.set(this.filteredTransactions().reduce((total, transaction) => total + transaction.amount, 0));
      this.type.set(segmentValue.id.toString());
      const params: Params = { count: 4, type: this.type() };
      this.getTransactions(params);
    }
  }

  goToTransactions() {
    this.navigationService.navigate([PENDING_TRANSACTIONS_ROUTE], {
      queryParams: {
        status: ['Waiting', 'Draft'],
        type: this.type(),
      },
    });
  }
}
